import React, { useState } from 'react';
import api from '../lib/api';

interface FormState {
  orgName: string;
  regNumber: string;
  address: string;
  city: string;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  password: string;
  passwordConfirm: string;
  notes: string;
}

const inp = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all bg-white';

const QuickRegisterPage: React.FC = () => {
  const [form, setForm] = useState<FormState>({
    orgName: '', regNumber: '', address: '', city: '',
    contactName: '', contactRole: '', contactEmail: '', contactPhone: '',
    password: '', passwordConfirm: '', notes: '',
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const set = (k: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.orgName || !form.contactName || !form.contactEmail || !form.password) {
      setError('שם ארגון, שם איש קשר, מייל וסיסמה הם שדות חובה');
      return;
    }
    if (form.password.length < 6) {
      setError('הסיסמה חייבת להכיל לפחות 6 תווים');
      return;
    }
    if (form.password !== form.passwordConfirm) {
      setError('הסיסמאות אינן תואמות');
      return;
    }

    setLoading(true);
    try {
      await api.post('/public/quick-register', {
        orgName: form.orgName,
        regNumber: form.regNumber,
        address: form.address,
        city: form.city,
        contactName: form.contactName,
        contactRole: form.contactRole,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        password: form.password,
        notes: form.notes,
      });
      setDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'שגיאה. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Success screen ── */
  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4" dir="rtl">
        <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">✅</span>
          </div>
          <h1 className="text-2xl font-black text-gray-900 mb-3">ההרשמה התקבלה!</h1>
          <p className="text-gray-500 leading-relaxed">
            תודה, <strong className="text-gray-800">{form.contactName}</strong>!<br />
            נציג מ-Amutech יצור איתך קשר בקרוב עם הצעת מחיר מותאמת אישית לארגון שלך.
          </p>
          <div className="mt-8 bg-blue-50 rounded-2xl p-4 text-sm text-blue-700 font-medium">
            📧 אישור נשלח לכתובת <strong>{form.contactEmail}</strong>
          </div>
        </div>
      </div>
    );
  }

  /* ── Form ── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900" dir="rtl">
      {/* Header */}
      <div className="text-center pt-10 pb-6 px-4">
        <img
          src="/amutech-logo.png"
          alt="Amutech"
          className="h-16 mx-auto mb-4 drop-shadow-lg"
        />
        <h1 className="text-white text-3xl font-black">הצטרפו ל-Amutech</h1>
        <p className="text-blue-200 text-base mt-2">מלאו את הפרטים וקבלו הצעת מחיר מותאמת</p>
      </div>

      {/* Card */}
      <div className="max-w-xl mx-auto px-4 pb-16">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Steps indicator */}
          <div className="bg-blue-50 border-b border-blue-100 px-6 py-4 flex items-center justify-center gap-6">
            {[
              { num: 1, label: 'פרטי הארגון' },
              { num: 2, label: 'איש קשר' },
              { num: 3, label: 'הגדרת סיסמה' },
            ].map(s => (
              <div key={s.num} className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-700 text-white text-xs font-black flex items-center justify-center">
                  {s.num}
                </div>
                <span className="text-sm font-semibold text-blue-800 hidden sm:block">{s.label}</span>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Section 1: Org */}
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">🏛️ פרטי הארגון</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">שם ארגון / עמותה <span className="text-red-500">*</span></label>
                  <input required value={form.orgName} onChange={set('orgName')}
                    className={inp} placeholder="עמותת..." />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">מספר רישום</label>
                    <input value={form.regNumber} onChange={set('regNumber')}
                      className={inp} placeholder="580..." />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">עיר</label>
                    <input value={form.city} onChange={set('city')}
                      className={inp} placeholder="תל אביב" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">כתובת</label>
                  <input value={form.address} onChange={set('address')}
                    className={inp} placeholder="רחוב ומספר" />
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">👤 פרטי איש קשר</p>
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">שם מלא <span className="text-red-500">*</span></label>
                    <input required value={form.contactName} onChange={set('contactName')}
                      className={inp} placeholder="ישראל ישראלי" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">תפקיד</label>
                    <input value={form.contactRole} onChange={set('contactRole')}
                      className={inp} placeholder="מנהל / מנכ״ל" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">מייל <span className="text-red-500">*</span></label>
                    <input required type="email" value={form.contactEmail} onChange={set('contactEmail')}
                      className={inp} placeholder="email@org.org" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">טלפון</label>
                    <input type="tel" value={form.contactPhone} onChange={set('contactPhone')}
                      className={inp} placeholder="050-..." />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Password */}
            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">🔐 הגדרת סיסמה למערכת</p>
              <p className="text-xs text-gray-500 mb-3 bg-blue-50 rounded-xl p-3">
                הסיסמה שתבחר תשמש אותך לכניסה למערכת לאחר אישור החשבון
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">סיסמה <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      required value={form.password} onChange={set('password')}
                      className={inp + ' pl-10'}
                      placeholder="לפחות 6 תווים"
                    />
                    <button type="button" onClick={() => setShowPass(s => !s)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPass ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">אימות סיסמה <span className="text-red-500">*</span></label>
                  <input
                    type={showPass ? 'text' : 'password'}
                    required value={form.passwordConfirm} onChange={set('passwordConfirm')}
                    className={inp + (form.passwordConfirm && form.password !== form.passwordConfirm ? ' border-red-400 ring-1 ring-red-400' : '')}
                    placeholder="הכנס שוב את הסיסמה"
                  />
                  {form.passwordConfirm && form.password !== form.passwordConfirm && (
                    <p className="text-xs text-red-500 mt-1">הסיסמאות אינן תואמות</p>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="border-t border-gray-100 pt-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1">הערות / בקשות מיוחדות</label>
              <textarea value={form.notes} onChange={set('notes')} rows={3}
                className={inp + ' resize-none'}
                placeholder="כל מידע נוסף שתרצה לשתף..." />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm font-semibold">
                ⚠️ {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-700 hover:bg-blue-800 disabled:opacity-60 text-white font-black py-4 rounded-2xl text-base transition-all shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  שולח...
                </span>
              ) : '🚀 שלח בקשת הצטרפות'}
            </button>

            <p className="text-center text-xs text-gray-400">
              בלחיצה על הכפתור אתה מסכים ל
              <a href="/terms" className="text-blue-600 hover:underline mx-1">תנאי השימוש</a>
              ו
              <a href="/privacy" className="text-blue-600 hover:underline mx-1">מדיניות הפרטיות</a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuickRegisterPage;
